/**
 * Cost centres, godowns and batch expiry.
 *
 * Every one of these can legitimately be empty — most small books use none of
 * them. An empty screen therefore explains that the books do not use the
 * feature, rather than showing a blank card that reads like a bug.
 */
import { useMemo, useState } from "react";

import { ShareBar } from "../components/ShareBar.js";
import { Card, Empty, Money, Row, Rows, Scroller, SectionTitle } from "../components/ui.js";
import {
  EXPIRING_SOON_DAYS,
  deriveBatches,
  deriveCostCentres,
  deriveGodowns,
} from "../derive/advanced.js";
import { formatDate, formatQty } from "../derive/reports.js";
import { useApp } from "../state.js";

/** The caveat that applies to both inventory screens. Written once. */
function MovementNote() {
  return (
    <p className="px-1 text-xs leading-relaxed text-ink-3">
      Built from voucher movement in this snapshot. Tally reports opening stock per item, not per
      godown or batch, so any stock lying here before the snapshot's first voucher is not counted.
    </p>
  );
}

export function CostCentres() {
  const { snapshot } = useApp();
  const report = useMemo(() => (snapshot ? deriveCostCentres(snapshot) : null), [snapshot]);

  if (!report) return <Empty>Nothing loaded.</Empty>;
  if (report.unused) {
    return <Empty>These books do not allocate anything to cost centres.</Empty>;
  }

  return (
    <div className="space-y-4">
      <ShareBar
        csv={[
          {
            label: "Export CSV",
            filename: "cost-centres",
            build: () => [
              ["Cost centre", "Category", "Debit", "Credit", "Net", "Entries"],
              ...report.rows.map((r) => [
                r.costCentre,
                r.category,
                r.debit.toFixed(2),
                r.credit.toFixed(2),
                r.net.toFixed(2),
                r.entries,
              ]),
            ],
          },
        ]}
      />

      <Card title={`${report.rows.length} cost centres`} flush>
        <Rows>
          {report.rows.map((row) => (
            <Row
              key={`${row.costCentre}-${row.category}`}
              title={row.costCentre}
              sub={`${row.category} · ${row.entries} ${row.entries === 1 ? "entry" : "entries"}`}
              right={<Money amount={row.net} />}
              rightSub={row.net < 0 ? "Dr" : "Cr"}
            />
          ))}
        </Rows>
      </Card>
    </div>
  );
}

export function Godowns() {
  const { snapshot } = useApp();
  const [open, setOpen] = useState<string | null>(null);
  const report = useMemo(() => (snapshot ? deriveGodowns(snapshot) : null), [snapshot]);

  if (!report) return <Empty>Nothing loaded.</Empty>;
  if (report.unused) return <Empty>No voucher in this snapshot names a godown.</Empty>;

  return (
    <div className="space-y-4">
      <MovementNote />

      {report.rows.map((godown) => (
        <div key={godown.godown}>
          <SectionTitle>{godown.godown}</SectionTitle>
          <Card flush>
            <Rows>
              {(open === godown.godown ? godown.items : godown.items.slice(0, 8)).map((item) => (
                <Row
                  key={item.stockItem}
                  title={item.stockItem}
                  sub={`in ${formatQty(item.inQty, item.unit)} · out ${formatQty(item.outQty, item.unit)}`}
                  right={formatQty(item.net, item.unit)}
                />
              ))}
            </Rows>
            {godown.items.length > 8 && (
              <button
                type="button"
                className="w-full px-4 py-3 text-left text-xs font-medium text-accent"
                onClick={() => setOpen(open === godown.godown ? null : godown.godown)}
              >
                {open === godown.godown
                  ? "Show fewer"
                  : `Show all ${godown.items.length} items →`}
              </button>
            )}
          </Card>
        </div>
      ))}
    </div>
  );
}

export function Batches() {
  const { snapshot, asOf } = useApp();
  const report = useMemo(
    () => (snapshot ? deriveBatches(snapshot, asOf) : null),
    [snapshot, asOf],
  );

  if (!report) return <Empty>Nothing loaded.</Empty>;
  if (report.unused) return <Empty>No voucher in this snapshot names a batch.</Empty>;

  return (
    <div className="space-y-4">
      <MovementNote />

      <ShareBar
        csv={[
          {
            label: "Export CSV",
            filename: "batches",
            build: () => [
              ["Item", "Batch", "Quantity", "Unit", "Expiry", "Days to expiry", "Status"],
              ...report.rows.map((r) => [
                r.stockItem,
                r.batch,
                r.qty,
                r.unit,
                r.expiryDate ?? "",
                r.daysToExpiry ?? "",
                r.status,
              ]),
            ],
          },
        ]}
      />

      {report.expired.length > 0 && (
        <div>
          <SectionTitle>Expired, still in stock</SectionTitle>
          <Card flush>
            <Rows>
              {report.expired.map((row) => (
                <BatchLine key={`${row.stockItem}-${row.batch}`} row={row} tone="bad" />
              ))}
            </Rows>
          </Card>
        </div>
      )}

      {report.expiring.length > 0 && (
        <div>
          <SectionTitle>Expiring within {EXPIRING_SOON_DAYS} days</SectionTitle>
          <Card flush>
            <Rows>
              {report.expiring.map((row) => (
                <BatchLine key={`${row.stockItem}-${row.batch}`} row={row} tone="warn" />
              ))}
            </Rows>
          </Card>
        </div>
      )}

      <div>
        <SectionTitle>All batches</SectionTitle>
        <Card flush>
          <Scroller>
            <table className="w-full min-w-[30rem] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3">
                  <th className="py-2 pr-3 pl-4 font-medium">Item</th>
                  <th className="py-2 pr-3 font-medium">Batch</th>
                  <th className="py-2 pr-3 text-right font-medium">Qty</th>
                  <th className="py-2 pr-4 font-medium">Expiry</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {report.rows.map((row) => (
                  <tr key={`${row.stockItem}-${row.batch}`}>
                    <td className="py-2 pr-3 pl-4">
                      <div className="max-w-[12rem] truncate text-ink">{row.stockItem}</div>
                    </td>
                    <td className="py-2 pr-3 text-ink-2">{row.batch}</td>
                    <td className="py-2 pr-3 text-right tabular">
                      {formatQty(row.qty, row.unit)}
                    </td>
                    <td className="py-2 pr-4 text-ink-3">
                      {row.expiryDate ? formatDate(row.expiryDate) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Scroller>
        </Card>
      </div>
    </div>
  );
}

function BatchLine({
  row,
  tone,
}: {
  row: { stockItem: string; batch: string; qty: number; unit: string; expiryDate?: string; daysToExpiry?: number };
  tone: "bad" | "warn";
}) {
  const days = row.daysToExpiry ?? 0;
  return (
    <Row
      title={row.stockItem}
      sub={
        `Batch ${row.batch}` +
        (row.expiryDate
          ? ` · ${days < 0 ? `expired ${-days} days ago` : `${days} days left`}`
          : "")
      }
      right={<span className={tone === "bad" ? "text-bad" : "text-warn"}>{formatQty(row.qty, row.unit)}</span>}
    />
  );
}
